import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from 'axios';
import _ from 'lodash'
import moment from 'moment';

import { LogManagerConsole } from 'Core/logManager';
import { ExceptionManager } from 'Core/logManager';

// Phase 1 RTK Slices Import
import { unitSelectors, addUnits, upsertManyWithRules as upsertUnits } from '../../realtime/redux/unitSlice';
import { upsertManyWithRules as upsertLastReadings, captureDiferentialUnitsSideEffect, addLastReadings } from '../../realtime/redux/lastReadingSlice';
import { tagSelectors, upsertManyWithRules as upsertTags } from '../../realtime/redux/tagSlice';
import { driverSelectors, upsertManyWithRules as upsertDrivers } from '../../realtime/redux/driverSlice';
import { assetSelectors, upsertManyWithRules as upsertAssets } from '../../realtime/redux/assetSlice';

import { getApiDate } from "../../../core/hooks";
import { clientQuery } from '../../../core/data/GraphQLApi';
import { endpoints } from '../../../core/defaultValues';
import { resetALL } from '../../../.globals/redux/actions';

import { catalogInitialResult, catalogProcessItems, clearRedux } from 'Redux/actions'
import { validateJsonObject } from "Core/data/Helpers";

//STATE INITIAL
const INIT_STATE = {
    loading: false,
    loadingInitialCompleted: false,
    loadingTags: false,
    loadingDrivers: false,
    loadingAssets: false,
    loadingDriversOnUnits: false,
    loadingAssetsOnUnits: false,

    loadingLandmarks: false,
    landmarks: [],

    loadingGeofences: false,
    geofences: [],
    jsonMap: [],
    loadedJsonFiles: [],    // { url: { landmarks: [], geofences: [] }}
};

const route = 'src/core/catalogs/redux/sliceRealtime'

// HELPERS
// Helper to update tags on landmarks and geofences
const updateTagsOnLandmarksAndGeofences = (entity, tags) => {
    let tagsOnEntity = [];
    let unitsWithTags = entity.map((item) => {
        tagsOnEntity = [];
        let tagIds = item.tagsId && item.tagsId.split(',') || []
        tagIds?.forEach((tagId) => {
            // Only create unitTags for tags in this batch
            const tag = tags.length ? tags.find(tag => tag.id == tagId) : null;
            if (tag) {
                tagsOnEntity.push({ ...tag, tagId: tagId })
            }
        });

        return {
            ...item,
            tags: tagsOnEntity
        }
    });

    return unitsWithTags;
}

export const getDevicesInitialData = createAsyncThunk(
    'catalog/getDevicesInitialData',
    async (payload, { rejectWithValue, dispatch }) => {
        // let user: IUserData | undefined;
        try {

            //start loading the old variabbles
            dispatch(clearRedux("CATALOG_REDUX", { loadingUnits: true }));

            const result = await clientQuery(`
                    query {
                        data: getInitialSmallData
                    }
                    `,
                {},
                endpoints.GRAPHQL_GENERAL_V2)

            LogManagerConsole('CATALOG', 'getDevicesRequest', result)
            let jsonData = result.data && JSON.parse(result.data)
            let readings = []
            let units = [];
            jsonData.forEach((item) => {

                let lastReading = {
                    rowId: -1,
                    ...item,
                    id: Number(item.id),
                    deviceId: Number(item.id),
                    haveLatLng: true,//Boolean((item.lastReading?.latitude && !isNaN(item.lastReading?.latitude) && parseFloat(item.lastReading?.latitude) != 0) && (item.lastReading?.longitude && !isNaN(item.lastReading?.longitude) && parseFloat(item.lastReading?.longitude) != 0)),
                    serverTime: getApiDate(item.serverTime),
                    unitTime: getApiDate(item.unitTime),
                    haveLatLng: Boolean((item.latitude && !isNaN(item.latitude) && parseFloat(item.latitude) != 0) && (item.longitude && !isNaN(item.longitude) && parseFloat(item.longitude) != 0)),
                    serverTime: getApiDate(item.serverTime),
                    unitTime: getApiDate(item.unitTime),
                    startingLoad: true,
                    esn: item.esn ?? '',
                    //LastMessageTime: getApiDate(item.LastMessageTime)
                    //LastMessageTime: getApiDate(item.lastReading.LastMessageTime)
                }

                readings.push(lastReading)

                let videoDevices = item.videoDevices && validateJsonObject(item.videoDevices) || []

                item = {
                    ...item,
                    id: Number(item.id),
                    hasVideo: videoDevices?.length > 0 ? true : false,
                    videoDevices: videoDevices || [],
                    timeZone: item.timeZone && validateJsonObject(item.timeZone) || null,
                    linkedUnit: videoDevices?.length > 0 ? videoDevices?.map(device => {
                        return {
                            id: device.id,
                            esn: device.esn,
                            manufacturerName: device.manufacturer
                        }
                    })[0] || null : null
                }


                units.push(item);
            });

            // Phase 3: Push directly to RTK so it merges with the existing IndexedDB persist Cache cleanly
            dispatch(addUnits(units));

            // Setup Last Sync tracker and dispatch readings
            sessionStorage.setItem("lastSyncLastReading", moment())
            dispatch(addLastReadings(readings));
            captureDiferentialUnitsSideEffect(readings); // Preserve old sync logic

            // Keep only the loading switches, avoid dispatching arrays to the legacy reducers
            // dispatch(catalogInitialResult({
            //     loadingUnits: false,
            //     loadingReadings: false,
            //     pmDevices: [],
            //     unitGroups: [],
            //     unitTags: [],
            //     tags: [],
            //     loadingTags: false
            // }))

            // Background Fetchers (Decoupled & Concurrent)
            dispatch(getTagsInitialData());

            // Defer Heavy Map Geometry downloads to unblock the main thread initially
            setTimeout(() => {
                dispatch(getLandmarksAndGeofences());
            }, 5000);

            //update loading the old variabbles
            dispatch(clearRedux("CATALOG_REDUX", { loadingUnits: false }));

            return

        } catch (exc) {
            ExceptionManager(exc, route, 'getDevicesInitialData');
            return rejectWithValue(exc);
        }
    }
);

export const getTagsInitialData = createAsyncThunk(
    'catalog/getTagsInitialData',
    async (payload, { rejectWithValue, dispatch, getState }) => {
        try {
            let _limit = 500
            let allTagsLoaded = false;
            let mergedTags = [];

            //start loading the old variabbles
            dispatch(clearRedux("CATALOG_REDUX", { loadingTags: true }));

            // Function to fetch tags in batches of 500
            const getTagsRequest = async (offset = 0, limit = _limit) => {
                const resultTags = await clientQuery(`
                    query Items($limit: Int, $offset: Int) {
                        tags: searchTags(limit: $limit, offset: $offset) {
                                                items {
                                id
                                label
                                color
                                parentId
                                path
                                hasChildren
                                disabled
                                unitsCount
                                driversCount
                                assetsCount
                                usersCount
                                landmarksCount
                                geofencesCount
                                dynamicFormsCount
                                tagPath
                                level
                            }
                            total
                        }
                    }
                    `,
                    { limit, offset },
                    endpoints.GRAPHQL_GENERAL_V2)

                LogManagerConsole('CATALOG', 'getTagsRequest', resultTags)

                let tags = resultTags?.tags?.items || [];

                // Process unit tags for this batch
                let state = getState();
                let units = unitSelectors.selectAll(state);
                let unitTags = [];

                //prevent mutating state directly
                units?.forEach((unit) => {
                    let tagIds = validateJsonObject(unit.tagIds) || [];
                    tagIds?.forEach((tagId) => {
                        // Only create unitTags for tags in this batch
                        const tag = tags.length ? tags.find(tag => tag.id == tagId) : null;
                        if (tag) {
                            unitTags.push({ unitId: unit.id, tagId: tagId })
                        }
                    })
                });

                // Dispatch tags and unitTags for this batch to update UI progressively
                dispatch(catalogProcessItems("UnitTags", unitTags))
                dispatch(upsertTags(tags))

                let newOffset = offset + _limit
                LogManagerConsole('CATALOG', 'newOffset getTagsInitialData', newOffset, resultTags?.tags?.total)

                // If there are more tags to fetch, call recursively
                if (newOffset < resultTags?.tags?.total) {
                    await getTagsRequest(newOffset)
                }

                // FINISHED LOADING ALL TAGS
                // When the loading of tags is finished, update the tags into units and drivers
                if (newOffset >= resultTags?.tags?.total) {
                    allTagsLoaded = true;
                }
            }

            // Start fetching tags from offset 0
            await getTagsRequest(0)

            dispatch(getDevices());
            dispatch(getDrivers());
            dispatch(getAssets());

            //update loading the old variabbles
            dispatch(clearRedux("CATALOG_REDUX", { loadingTags: false }));

            // FINISHED LOADING ALL TAGS
            // When the loading of tags is finished, update the tags into units and drivers
            if (allTagsLoaded) {
                let state = getState();

                let units = unitSelectors.selectAll(state);
                let mergedTags = tagSelectors.selectAll(state);

                //update tags on units
                let tagsOnUnit = [];
                let unitsWithTags = [];
                units.forEach((unit) => {
                    tagsOnUnit = [];
                    let tagIds = validateJsonObject(unit.tagIds) || [];
                    tagIds?.forEach((tagId) => {
                        // Only create unitTags for tags in this batch
                        const tag = mergedTags.length ? mergedTags.find(tag => tag.id == tagId) : null;
                        if (tag) {
                            tagsOnUnit.push({ ...tag, tagId: tagId })
                        }
                    })

                    if (tagsOnUnit.length > 0) {
                        unitsWithTags.push({
                            id: unit.id,
                            tags: tagsOnUnit
                        })
                    }
                });

                // Finally, update tags on landmarks and geofences
                const landmarks = state.catalogsRealtimeRedux.landmarks || [];
                const geofences = state.catalogsRealtimeRedux.geofences || [];
                if (landmarks?.length > 0) {
                    const landmarksWithTags = updateTagsOnLandmarksAndGeofences(landmarks, mergedTags);
                    dispatch(loadLandmarks(landmarksWithTags));
                }
                if (geofences?.length > 0) {
                    const geofencesWithTags = updateTagsOnLandmarksAndGeofences(geofences, mergedTags);
                    dispatch(loadGeofences(geofencesWithTags));
                }
                // console.log('unitsWithTags', unitsWithTags)
                unitsWithTags.length > 0 && dispatch(upsertUnits(unitsWithTags));
            }

            return {
                success: true,
                allTagsLoaded: true,
                tags: mergedTags
            }

        } catch (exc) {
            ExceptionManager(exc, route, 'getTagsInitialData');
            return rejectWithValue(exc);
        }
    }
);

export const getDevices = createAsyncThunk(
    'catalog/getDevices',
    async (payload, { rejectWithValue, dispatch }) => {
        // let user: IUserData | undefined;
        try {

            //start loading the old variabbles
            dispatch(clearRedux("CATALOG_REDUX", { loadingReadings: true }));

            LogManagerConsole('CATALOG', 'getDevices')
            let _limit = 500

            const getDevicesRequest = async (offset = 0, limit = _limit) => {
                const result = await clientQuery(`
                    query Items($limit: Int, $offset: Int) {
                        data: searchUnits(limit: $limit, offset: $offset) {
                            items {
                                id
                                driverName
                                driverId
                                defaultDriverId
                                driverExternalId
                                deviceCategory
                                desiredStarterState
                                esn
                                imei
                                label
                                typeId
                                useEcuSpeed
                                unitVersionId
                                unitVersion {
                                    odometerEnable
                                    hoursEnable
                                    starter
                                    supportedOutputs
                                }
                                                    timeZone {
                                    id
                                    name
                                }
                                                    lastReading {
                                    rowId
                                    landmark_id
                                                        address {
                                        address
                                        city
                                        state
                                        formatted
                                        zip
                                    }
                                    driverId
                                    driverName
                                    deviceLabel
                                    make
                                    model
                                    vin
                                    year
                                    deviceId
                                    ecuSpeed
                                    ecuVin
                                    unitTime
                                    ecuEngineOdometer
                                    desiredStarterState
                                    starterSupported
                                    starterDisabled
                                    ecuEngineHours
                                    engineHours
                                    engineOdometer
                                    latitude
                                    longitude
                                    speed
                                    speedLimit
                                    LastMessageTime
                                    serverTime
                                    heading
                                    unitType
                                    eventCode
                                    eventName
                                    deviceData
                                    temperatures
                                }
                                linkedMode
                                linkedUnitId
                                                    linkedUnit {
                                    id
                                    label
                                    manufacturerName
                                    esn
                                                        timeZone {
                                        id
                                        name
                                    }
                                                        unitVersion {
                                        unitVersionProduct
                                        tow
                                    }
                                }
                                offsetEcuEngineHours
                                offsetEcuEngineOdometer
                                offsetEngineHours
                                offsetEngineOdometer
                                manufacturerId
                                manufacturerName
                                vbus
                                jbus
                                useEcuEngineHours
                                useEcuOdometer
                                useEcuVin
                                vehicleStatusId
                                isOffline
                                maintenanceWarningEventsCount
                                sdCard
                                driverBehavior
                                icon
                                deviceModel {
                                    id
                                    name
                                }
                                inputs {
                                    position
                                    labelId
                                    onLabel
                                    offLabel
                                }
                            }
                            total
                        }
                    }
                    `,
                    { limit, offset },
                    endpoints.GRAPHQL_GENERAL_V2)

                LogManagerConsole('CATALOG', 'getDevicesRequest', result)

                let readings = []
                let units = [];

                result.data.items.forEach((item) => {
                    if (item?.lastReading && item.lastReading != undefined) {

                        let lastReading = {
                            ...item.lastReading,
                            id: item.id,
                            deviceId: item.id,
                            landmarkId: item.lastReading?.landmark_id,
                            haveLatLng: Boolean((item.lastReading?.latitude && !isNaN(item.lastReading?.latitude) && parseFloat(item.lastReading?.latitude) != 0) && (item.lastReading?.longitude && !isNaN(item.lastReading?.longitude) && parseFloat(item.lastReading?.longitude) != 0)),
                            serverTime: getApiDate(item.lastReading.serverTime),
                            unitTime: getApiDate(item.lastReading.unitTime),
                            LastMessageTime: getApiDate(item.lastReading.LastMessageTime),
                            startingLoad: false,
                            ignoreValidation: true,
                            linkedUnitId: item?.linkedUnit ? { ...item.linkedUnit, unitVersionProduct: item.linkedUnit?.unitVersion?.unitVersionProduct } : {},
                            tow: item.linkedUnit?.unitVersion?.tow
                        }
                        //readings.push({ entityName: 'LastReading', payload: lastReading })
                        readings.push(lastReading)

                        // if (item.lastReading != undefined) {
                        //     delete item.lastReading;
                        // }
                    }

                    //units.push({ entityName: 'Unit', payload: item });
                    units.push({ ...item, starter: item.unitVersion?.starter ? true : false, unitType: item?.lastReading?.unitType || '' });

                })

                let differential = []
                if (offset == 0)
                    differential = []
                else {
                    let differentialString = sessionStorage.getItem("unitsDiferential")
                    differential = JSON.parse(differentialString)
                }

                differential.push(readings.map(x => { return { deviceId: x.deviceId, unitTime: getApiDate(x.unitTime) } }))
                sessionStorage.setItem("unitsDiferential", JSON.stringify(differential))

                dispatch(upsertUnits(units));
                dispatch(upsertLastReadings(readings));

                let newOffset = offset + _limit
                LogManagerConsole('CATALOG', 'newOffset', newOffset, result.data.total)
                if (newOffset < result.data.total) {
                    await getDevicesRequest(newOffset)
                }

            }

            await getDevicesRequest(0)

            //start update the old variabbles
            dispatch(clearRedux("CATALOG_REDUX", { loadingReadings: false }));

            dispatch(updateDriverIconsFromUnits());

            return

        } catch (exc) {
            ExceptionManager(exc, route, 'getDevices');
            return rejectWithValue(exc);
        }
    }
);

export const getDrivers = createAsyncThunk(
    'catalog/getDrivers',
    async (payload, { rejectWithValue, dispatch, getState }) => {
        // let user: IUserData | undefined;
        try {

            LogManagerConsole('DCD', 'getDrivers')
            let _limit = 500;
            let allDrivers = false;

            const getRequest = async (offset = 0, limit = _limit) => {
                const result = await clientQuery(`
                    query Items($limit: Int, $offset: Int) {
                        data: searchDrivers(limit: $limit, offset: $offset){
                            items {
                                id
                                name
                                unitId
                                externalId
                                status
                                avatarFileId
                                companyId
                                contactInfo
                                idTimeZone
                                email
                                avatarFile {
                                    id
                                    fileKey
                                    repositoryPath
                                }
                                fsmDriver {
                                    id
                                    connected
                                    lastConnected
                                    username
                                }
                                tempAvatarUrl
                                tagIds
                                driverStatus
                                supervisorId
                                supervisor {
                                    firstName
                                    lastName
                                }
                                unit {
                                    id
                                    label
                                }
                                timeZone {
                                    id
                                    title
                                }
                                keyfob {
                                    id
                                    hex
                                }
                                empNum
                                driverPin
                                licenseNumber
                                licenseCountry
                                licenseState
                                licenseExpDate
                                hosRulesetIds
                                hosExempt
                                hosExemptComment
                                specialDutyStatuses
                            }
                            total
                        }
                    }
                    `,
                    { limit, offset },
                    endpoints.GRAPHQL_GENERAL_V2)

                LogManagerConsole('DCD', 'getDrivers', result)

                // Process driver tags for this batch
                let state = getState();
                //let currentDrivers = driverSelectors.selectAll(state);
                let tags = tagSelectors.selectAll(state);
                let units = unitSelectors.selectAll(state);

                // Merge new drivers with existing ones in state
                let newDrivers = result?.data?.items?.length > 0 ? result.data.items.map(item => ({ ...item, driverId: item?.id })) : [];

                let driversWithTags = newDrivers.map((driver) => {
                    let driverTags = [];
                    let tagIds = validateJsonObject(driver?.tagIds);
                    //find the tags for the driver
                    tagIds?.forEach((tagId) => {
                        let tag = tags.find(tag => tag.id == tagId);
                        if (tag) {
                            driverTags.push({ ...tag, tagId: tagId })
                        }
                    })

                    //find the unit for the driver
                    let unit = units.find(unit => unit.id == driver?.unitId);
                    return {
                        ...driver,
                        driverId: driver?.id,
                        tags: driverTags,
                        unit: unit ? { label: unit?.label, id: unit?.id, icon: unit?.icon } : null,
                        connected: driver?.fsmDriver ? driver.fsmDriver.connected : null,
                        lastConnected: driver?.fsmDriver ? driver.fsmDriver.lastConnected : null,
                        fsmUsername: driver?.fsmDriver ? driver.fsmDriver.username : null,
                        fsmDriver: driver?.fsmDriver || null,
                        idTimeZone: driver?.idTimeZone || null,
                        supervisor: driver?.supervisor || null,
                    }
                });

                dispatch(upsertDrivers(driversWithTags || []))

                let newOffset = offset + _limit
                LogManagerConsole('DCD', 'newOffset getDrivers', newOffset, result.data.total)

                if (newOffset < result.data.total) {
                    await getRequest(newOffset)
                } else {
                    //FINISHED LOADING ALL DRIVERS
                    allDrivers = true;
                }

            }

            await getRequest(0)

            if (allDrivers) {
                dispatch(enrichUnitsWithDrivers())

                dispatch(updateDriverIconsFromUnits())
            }

            return

        } catch (exc) {
            ExceptionManager(exc, route, 'getDrivers');
            return rejectWithValue(exc);
        }
    }
);

export const getAssets = createAsyncThunk(
    'catalog/getAssets',
    async (payload, { rejectWithValue, dispatch, getState }) => {

        try {
            let _limit = 500;
            let allAssets = false;

            const getRequest = async (offset = 0, limit = _limit) => {
                const result = await clientQuery(`
                    query Items($limit: Int, $offset: Int) {
                    data: searchAssets(limit: $limit, offset: $offset) {
                                            items {
                            id
                            label
                            unitId
                                                unit {
                                id
                                label
                            }
                            assetTypeId
                                                assetType {
                                entityId
                                label
                            }
                            assetSubtypeId
                                                assetSubtype {
                                id
                                name
                            }
                            vin
                            ecuVin
                            inspectionDeviceType
                            externalId
                            isActive
                            tagIds
                            make
                            model
                            year
                            engine
                            mpgHighway
                            mpgCity
                            licensePlate
                            inspectionTagId
                            chassisSerialNumber
                            engineSerialNumber
                            maintenanceCompany
                            powerUnitNumber
                            registrationCountry
                            registrationExpiration
                            registrationState
                        }
                        total
                    }
                }
                `,
                    { limit, offset },
                    endpoints.GRAPHQL_GENERAL_V2)

                LogManagerConsole('CATALOG_ASSETS', `getAssets batch ${offset} -${offset + limit} `, result?.data?.items?.length)

                // Process asset tags for this batch
                let state = getState();
                let tags = tagSelectors.selectAll(state);

                // Merge new assets with existing ones in state and add tags
                let newAssets = result?.data?.items?.length > 0
                    ? result.data.items.map(item => {
                        let assetTags = [];
                        let tagIds = validateJsonObject(item?.tagIds);
                        tagIds?.forEach((tagId) => {
                            let tag = tags.find(tag => tag.id == tagId);
                            if (tag) {
                                assetTags.push({ ...tag, tagId: tagId })
                            }
                        })

                        return {
                            ...item,
                            tags: assetTags
                        }
                    })
                    : [];


                dispatch(upsertAssets(newAssets || []))

                let newOffset = offset + _limit
                LogManagerConsole('CATALOG_ASSETS', 'newOffset getAssets', newOffset, '/', result.data.total)

                if (newOffset < result.data.total) {
                    await getRequest(newOffset)
                } else {
                    //FINISHED LOADING ALL ASSETS
                    allAssets = true;
                }
            }

            await getRequest(0)

            if (allAssets) {
                dispatch(enrichUnitsWithAssets())
            }

            return

        } catch (exc) {
            ExceptionManager(exc, route, 'getAssets');
            return rejectWithValue(exc);
        }
    }
);

export const getLandmarksAndGeofences = createAsyncThunk(
    'catalog/getLandmarksAndGeofences',
    async (payload, { rejectWithValue, dispatch, getState }) => {
        try {
            LogManagerConsole('CATALOG', 'getLandmarksAndGeofences');

            const result = await clientQuery(
                `
                query getJsonMap($newFile: Boolean){
                    files: getJsonMap(newFile: $newFile) {
                        entityName
                        jsonPath {
                            urls
                        }
                        generatedAt
                        changedAt
                    }
                }
                `,
                { newFile: true },
                endpoints.GRAPHQL_GENERAL_V2
            )

            LogManagerConsole('CATALOG', 'getLandmarksAndGeofences', result);

            let state = getState();
            let landmarks = state?.catalogsRealtimeRedux?.landmarks || [];
            let geofences = state?.catalogsRealtimeRedux?.geofences || [];
            let loadedJsonFiles = state?.catalogsRealtimeRedux?.loadedJsonFiles || [];

            // Process asset tags for this batch
            let tags = tagSelectors.selectAll(state) || [];

            let newFiles = [];
            let existingLandmarkIds = new Set();
            let existingGeofenceIds = new Set();

            if (result && result?.files?.length) {
                const { files } = result;

                for (let i = 0; i < files?.length; i++) {
                    const file = files[i];
                    const url = file.jsonPath?.urls[0];
                    newFiles.push(url);
                    if (loadedJsonFiles.includes(url)) {
                        LogManagerConsole('CATALOG', `File already loaded from cache: ${url} `);
                        //finish all process because the files are loaded
                        continue;
                    }

                    if (file?.entityName == "Geofence") {
                        //load new geofences
                        geofences = [];
                        existingGeofenceIds = new Set();
                    } else if (file?.entityName == "Landmark") {
                        //starting load new landmarks
                        landmarks = [];
                        existingLandmarkIds = new Set();
                    }

                    const dataRead = await axios.get(url);
                    if (dataRead) {
                        for (let j = 0; j < dataRead?.data?.length; j++) {
                            let item = dataRead?.data[j];
                            let otags = [];
                            if (item?.t) {
                                const tagsId = item?.t?.split(",");
                                otags = tags?.filter((tag) => tagsId?.includes(`${tag?.id}`));
                            }

                            if (file.entityName == "Geofence") {
                                if (existingGeofenceIds.has(item.i)) {
                                    continue; // Skip duplicate geofence
                                }
                                existingGeofenceIds.add(item.i);
                                const coordinates = item?.c?.length > 0 ? item.c.map(([lat, lon]) => ({
                                    latitude: lat,
                                    longitude: lon
                                })) : [];
                                geofences.push({
                                    id: item.i,
                                    name: item.n,
                                    lat: item.lt,
                                    lng: item.lg,
                                    radius: item.r,
                                    color: item.cl,
                                    shape: item.s,
                                    contacts: item.con,
                                    assignedUnitsCount: item.auc,
                                    alertOnType: item.aot, //"In/Out" //review is necesary this info
                                    insideDurationMins: item.idm, //review is necesary this info
                                    tagsId: item.t,
                                    tags: otags,
                                    isAlert: item.ia,
                                    coordinates: coordinates
                                })
                            } else if (file.entityName == "Landmark") {
                                if (existingLandmarkIds.has(item.i)) {
                                    continue; // Skip duplicate landmark
                                }
                                existingLandmarkIds.add(item.i);
                                const coordinates = item?.c?.length > 0 ? item.c.map(([lat, lon]) => ({
                                    latitude: lat,
                                    longitude: lon
                                })) : [];
                                landmarks.push({
                                    id: item.i,
                                    name: item.n,
                                    latitude: item.lt,
                                    longitude: item.lg,
                                    radius: item.r,
                                    color: item.cl,
                                    shape: item.s,
                                    iconName: item.ic,
                                    address: item.add,
                                    note: item.nt,
                                    tagsId: item.t,
                                    tags: otags,
                                    coordinates: coordinates
                                });
                            }

                        }
                    }
                }
            }

            const response = {
                landmarks,
                geofences,
                jsonMap: result?.files,
                loadInformation: true,
                loadedJsonFiles: newFiles
            }

            return response;
        } catch (exc) {
            ExceptionManager(exc, route, 'getJsonMap');
            return rejectWithValue(exc);
        }
    }
);

export const enrichUnitsWithDrivers = createAsyncThunk(
    'catalog/enrichUnitsWithDrivers',
    async (payload, { rejectWithValue, dispatch, getState }) => {
        try {
            LogManagerConsole('CATALOG', 'enrichUnitsWithDrivers - Start enriching units with driver data');

            let state = getState();
            let units = unitSelectors.selectAll(state);
            let drivers = driverSelectors.selectAll(state);

            // Create a Map for O(1) driver lookups
            const driversMap = new Map(drivers.map(driver => [driver.id, driver]));

            // Enrich units with driver information
            let unitsWithDriverInfo = units.map(unit => {
                // Find driver by driverId, defaultDriverId, or unit.driver?.id
                const driverId = unit.driverId;
                const driver = driverId ? driversMap.get(driverId) : null;

                if (driver) {
                    return {
                        ...unit,
                        driver: {
                            id: driver.id,
                            name: driver.name,
                            avatarFileId: driver.avatarFileId,
                            avatarFile: driver.avatarFile ? {
                                id: driver.avatarFile.id,
                                fileKey: driver.avatarFile.fileKey,
                                repositoryPath: driver.avatarFile.repositoryPath
                            } : null,
                            tempAvatarUrl: driver.tempAvatarUrl
                        }
                    };
                } else {
                    // Keep unit without driver if no driver found
                    return {
                        ...unit,
                        driver: null
                    };
                }
            });

            // Update units natively in RTK
            dispatch(upsertUnits(unitsWithDriverInfo));

            LogManagerConsole('CATALOG', 'enrichUnitsWithDrivers - Completed', unitsWithDriverInfo.length, 'units enriched');

            return {
                success: true,
                enrichedUnitsCount: unitsWithDriverInfo.length
            };

        } catch (exc) {
            ExceptionManager(exc, route, 'enrichUnitsWithDrivers');
            return rejectWithValue(exc);
        }
    }
);

export const enrichUnitsWithAssets = createAsyncThunk(
    'catalog/enrichUnitsWithAssets',
    async (payload, { rejectWithValue, dispatch, getState }) => {
        try {
            LogManagerConsole('CATALOG', 'enrichUnitsWithAssets - Start enriching units with asset data');

            let state = getState();
            let units = unitSelectors.selectAll(state);
            let assets = assetSelectors.selectAll(state);
            let tags = tagSelectors.selectAll(state);

            // create a Map for O(1) unit lookups
            const unitsMap = new Map(units.map(unit => [unit.id, unit]));
            const tagsMap = new Map(tags.map(tag => [tag.id, tag]));

            // Enrich units with asset information
            const unitsWithAssets = [];
            const tagAssets = [];
            assets.forEach(asset => {
                const unit = unitsMap.get(asset.unitId);
                if (unit) {
                    unitsWithAssets.push({
                        ...unit,
                        asset: {
                            vin: asset.vin || null,
                            ecuVin: asset.ecuVin || null,
                            make: asset.make || null,
                            model: asset.model || null,
                            year: asset.year || null,
                            engine: asset.engine || null,
                            mpgHighway: asset.mpgHighway || null,
                            mpgCity: asset.mpgCity || null,
                            licensePlate: asset.licensePlate || null
                        }
                    });
                }


                let tagIds = validateJsonObject(asset?.tagIds);
                let assetTags = [];
                tagIds?.forEach((tagId) => {
                    const tag = tagsMap.get(asset.tagId);
                    if (tag) {
                        assetTags.push({ ...tag, tagId: tagId })
                    }
                })
                if (assetTags.length > 0) {
                    tagAssets.push({ ...asset, tags: assetTags });
                }
            });

            // Update units and assets natively in RTK (assorted rules applied on slice)
            dispatch(upsertUnits(unitsWithAssets));
            dispatch(upsertAssets(tagAssets));

            LogManagerConsole('CATALOG', 'enrichUnitsWithAssets - Completed', unitsWithAssets?.length, 'units enriched');

        } catch (exc) {
            ExceptionManager(exc, route, 'enrichUnitsWithAssets');
            return rejectWithValue(exc);
        }
    }
);

export const updateDriverIconsFromUnits = createAsyncThunk(
    'catalog/updateDriverIconsFromUnits',
    async (payload, { rejectWithValue, dispatch, getState }) => {
        try {
            LogManagerConsole('CATALOG', 'updateDriverIconsFromUnits - Start');

            let state = getState();
            let units = unitSelectors.selectAll(state);
            let drivers = driverSelectors.selectAll(state);

            // If there are no drivers or units loaded, exit early
            if (drivers.length === 0 || units.length === 0) {
                LogManagerConsole('CATALOG', 'updateDriverIconsFromUnits - Skipped (no drivers or units)');
                return { success: false, reason: 'No drivers or units loaded' };
            }

            // Create a Map for O(1) unit lookups by id - only units with icon
            const unitsMap = new Map(
                units
                    .filter(unit => unit?.icon) // Only units with icon
                    .map(unit => [unit.id, unit])
            );

            let driversWithIcons = [];
            // Update drivers with unit icon
            drivers.map(driver => {
                // If the driver has an associated unit.id, find the unit and update the icon
                if (driver?.unit?.id) {
                    const unit = unitsMap.get(driver.unit.id);
                    if (unit && unit?.icon) {
                        driversWithIcons.push({
                            ...driver,
                            unit: {
                                ...driver.unit,
                                label: unit?.label,
                                icon: unit?.icon
                            }
                        })
                    };
                }
            });

            // Only update if there were changes
            const driversUpdated = driversWithIcons.filter(d => d.unit?.icon).length;

            if (driversWithIcons.length > 0) {
                dispatch(upsertDrivers(driversWithIcons));
                LogManagerConsole('CATALOG', 'updateDriverIconsFromUnits - Completed', driversUpdated, 'drivers updated with icons');
            }

            return {
                success: true,
                updatedDriversCount: driversUpdated
            };

        } catch (exc) {
            ExceptionManager(exc, route, 'updateDriverIconsFromUnits');
            return rejectWithValue(exc);
        }
    }
);

/*
export const getDifferentialDevices = createAsyncThunk(
    'catalog/getDifferentialDevices',
    async (payload, { rejectWithValue, dispatch }) => {
        // let user: IUserData | undefined;
        try {

            const { refreshTime, messages } = payload
            LogManagerConsole('DC_Diferencial', 'getDifferentialDevices', refreshTime)

            let devices = sessionStorage.getItem("unitsDiferential")
            devices = devices && JSON.parse(devices) || []
            devices = devices.filter(x => {
                let unitTime = moment(Number(x.unitTime))
                return unitTime.unix() > moment().add(-12, "hours").unix() && unitTime.unix() <= moment().add((-refreshTime), "seconds").unix()
            })

            const data = await clientQuery(
                `
                    query searchDifferentialReadings($devices: [DeviceLastReportInput]){
    readings: searchDifferentialReadings(devices: $devices){
        deviceId
        deviceLabel
        make
        model
        vin
        year
        unitTime
        eventCode
        eventName
        latitude
        longitude
        engineHours
        engineOdometer
        starterSupported
        starterDisabled
        dtc
        desiredStarterState
        heading
        serverTime
                            address {
            address
            city
            formatted
            state
            zip
        }
        ecuEngineHours
        ecuEngineOdometer
        ecuVin
        speed
        ecuSpeed
        unitType
        landmark_id
        landmarkname
        deviceData
                            trail{
            lat
            lon
            timestamp
        }
    }
}
` ,
                { devices: devices },
                endpoints.GRAPHQL_GENERAL);



            let readings = data.readings || []
            LogManagerConsole("DC_Diferencial", "catalogInitialRefreshRequest", readings.length);
            while (readings.length > 100) {
                let sendReadings = readings.slice(0, 99)
                readings = readings.slice(99, readings.length)
                LogManagerConsole("DC_Diferencial", "catalogInitialRefreshRequest", "Send", sendReadings.length);
                LogManagerConsole("DC_Diferencial_Detail", "catalogInitialRefreshRequest", "Send", sendReadings);
                dispatch(processItems({ items: sendReadings, messages }))
            }

            LogManagerConsole("DC_Diferencial", "catalogInitialRefreshRequest", "Send", readings.length);
            LogManagerConsole("DC_Diferencial_Detail", "catalogInitialRefreshRequest", "Send", readings);
            dispatch(processItems({ items: readings, messages }))

        } catch (exc) {
            ExceptionManager(exc, route, 'getDifferentialDevices');
            return rejectWithValue(exc);
        }
    }
);

export const getDifferentialPushNotifications = createAsyncThunk(
    'catalog/getDifferentialPushNotifications',
    async ({ sessionId, accountId, dateRange, messages }, { rejectWithValue, dispatch }) => {
        // let user: IUserData | undefined;
        try {

            LogManagerConsole('DC_Diferencial', 'getDifferentialPushNotifications', sessionId, accountId, dateRange)

            const data = await clientQuery(
                `
                   query searchPushNotifications($sessionId: String, $accountId: String, $dateRange: AWSDateTimeRange, $limit: Int, $nextToken: String) {
    pushNotifications: searchPushNotifications(sessionId: $sessionId, accountId: $accountId, dateRange: $dateRange, limit: $limit, nextToken: $nextToken) {
                        items {
            id
            sessionId
            accountId
            payload
            createdOn
        }
        nextToken
    }
}
` ,
                { sessionId, accountId, dateRange, limit: 100 },
                endpoints.GRAPHQL_GENERAL);



            LogManagerConsole("DC_Diferencial", "getDifferentialPushNotifications", data);
            let pushNotifications = []

            if (data.pushNotifications)
                for (let itemString of data.pushNotifications.items) {
                    let item
                    try {
                        //TODO: Temporal to support Reports
                        item = JSON.parse(JSON.parse(itemString.payload)).message
                        dispatch(processItems({ items: [item], messages }))
                    } catch (e) {
                        item = JSON.parse(itemString.payload)
                        dispatch(processItems({ items: [item], messages }))
                    }
                    pushNotifications.push(item)
                }

        } catch (exc) {
            ExceptionManager(exc, route, 'getDifferentialPushNotifications');
            return rejectWithValue(exc);
        }
    }
);


export const processItems = createAsyncThunk('catalogs/processItems', async ({ items, messages, processors = {} }, { rejectWithValue, dispatch, getState }) => {
    try {

        let state = getState()
        let catalogsRealtimeRedux = state.catalogsRealtimeRedux
        let allProcessors = { ...processors, ...processorsClasss }

        //if (catalogsRealtimeRedux.loadingInitialCompleted) {
            LogManagerConsole('DC4', 'processItems', "processItems", items, allProcessors)

            let entitiesToProcess = useGetUniqueValues(items, 'entityName')
            for (const entityToProcess of entitiesToProcess) {
                LogManagerConsole('DC4', "processItems", "entityToProcess", entityToProcess)
                let itemsToProcess = items.filter((x) => x.entityName == entityToProcess).map((x) => x.payload)
                LogManagerConsole('DC4', "processItems", "entityToProcess = Items", itemsToProcess)
                let processorObj = allProcessors[entityToProcess]
                if (processorObj) {
                    LogManagerConsole('DC4', "processItems", "processorObj", processorObj)
                    let processor = new processorObj()
                    LogManagerConsole('DC4', "processItems", "processor", processor)
                    let resultProcess = processor.executeArray(itemsToProcess, catalogsRealtimeRedux, state, processors, messages, dispatch)
                    LogManagerConsole('DC4', "processItems", "resultProcess", resultProcess)
                    catalogsRealtimeRedux = { ...catalogsRealtimeRedux, ...resultProcess }
                }
                else
            }

            //  .      …<  if (lastReadings.length > 0) {
            //     processorObj = processors['LastReading']
            //     processor = new processorObj()
            //     LogManager.Console("processItems", "LastReading", lastReadings)
            //     state.lastReadings = processor.executeArray(lastReadings, state).lastReadings
            // }

            // if (units.length > 0) {
            //     processorObj = processors['Unit']
            //     processor = new processorObj()
            //     LogManager.Console("processItems", "Unit", lastReadings)
            //     state.units = processor.executeArray(units, state).units
            // }

            // if (vehicles.length > 0) {
            //     processorObj = processors['VehicleInformation']
            //     processor = new processorObj()
            //     LogManager.Console("processItems", "Unit", lastReadings)
            //     state.units = processor.executeArray(vehicles, state).units
            // }

            // if (notifications.length > 0) {
            //     for (const notification of notifications) {
            //         const ClassModel = processors[notification.type?.toUpperCase()]
            //         if (ClassModel) {
            //             LogManager.Console('Catalog2', "FindClass")
            //             const classModel = new ClassModel()
            //             LogManager.Console('Catalog2', "FindClass", classModel)
            //             await classModel.execute(notification.data, state, dispatch, messages)
            //         }
            //         else {
            //             LogManager.Console('Catalog2', "class not found")
            //         }
            //     }
            // }

            return catalogsRealtimeRedux
        /* }
        else
            return {} ///


    } catch (exc) {
        ExceptionManager(exc, route, 'processItems');
        return rejectWithValue(exc)
    }
});
*/

//Slice
const catalogsRealtimeRedux = createSlice({
    name: 'common/catalogs',
    initialState: INIT_STATE,
    reducers: {
        catalogLoadings(state, action) {
            return { ...state, ...action.payload }
        },
        // setDevicesProgress(state, action) {
        //     state.units = [...state.units, ...action.payload.units]
        //     state.readings = [...state.readings, ...action.payload.readings]
        //     state.loadingUnits = false;
        // },
        // setDriversProgress(state, action) {
        //     state.drivers = [...state.drivers, ...action.payload]
        // },
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE }
        },
        loadLandmarks(state, action) {
            state.landmarks = action.payload || []
        },
        loadGeofences(state, action) {
            state.geofences = action.payload || []
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE)


        builder.addCase(getDevicesInitialData.pending, (state) => {
            state.loading = true;
        })
        builder.addCase(getDevicesInitialData.fulfilled, (state, { payload }) => {
            state.loading = false;
        })
        builder.addCase(getDevicesInitialData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        })


        builder.addCase(getTagsInitialData.pending, (state) => {
            state.loadingTags = true;
        })
        builder.addCase(getTagsInitialData.fulfilled, (state, { payload }) => {
            state.loadingTags = false;
        })
        builder.addCase(getTagsInitialData.rejected, (state, action) => {
            state.loadingTags = false;
            state.error = action.error.message;
        })



        builder.addCase(getDevices.pending, (state) => {
            state.loadingInitialCompleted = true;
        })
        builder.addCase(getDevices.fulfilled, (state, { payload }) => {
            state.loadingInitialCompleted = false;
        })
        builder.addCase(getDevices.rejected, (state, action) => {
            state.loadingInitialCompleted = false;
            state.error = action.error.message;
        })

        builder.addCase(getDrivers.pending, (state) => {
            state.loadingDrivers = true;
        })
        builder.addCase(getDrivers.fulfilled, (state, { payload }) => {
            state.loadingDrivers = false;
        })
        builder.addCase(getDrivers.rejected, (state, action) => {
            state.loadingDrivers = false;
            state.error = action.error.message;
        })

        builder.addCase(getAssets.pending, (state) => {
            state.loadingAssets = true;
        })
        builder.addCase(getAssets.fulfilled, (state, { payload }) => {
            state.loadingAssets = false;
        })
        builder.addCase(getAssets.rejected, (state, action) => {
            state.loadingAssets = false;
            state.error = action.error.message;
        })

        builder.addCase(getLandmarksAndGeofences.pending, (state) => {
            state.loadingLandmarks = true;
            state.loadingGeofences = true;
        });
        builder.addCase(getLandmarksAndGeofences.fulfilled, (state, { payload }) => {
            state.loadingLandmarks = false;
            state.loadingGeofences = false;
            if (payload?.loadInformation) {
                state.loadedJsonFiles = payload?.loadedJsonFiles;
                state.landmarks = payload?.landmarks;
                state.geofences = payload?.geofences;
                state.jsonMap = payload?.jsonMap;
            }
            state.error = null;
        });
        builder.addCase(getLandmarksAndGeofences.rejected, (state, action) => {
            state.loadingLandmarks = false;
            state.loadingGeofences = false;
            state.error = action.error.message
        });

        builder.addCase(enrichUnitsWithDrivers.pending, (state) => {
            state.loadingDriversOnUnits = true;
            LogManagerConsole('CATALOG', 'enrichUnitsWithDrivers - pending');
        })
        builder.addCase(enrichUnitsWithDrivers.fulfilled, (state, { payload }) => {
            state.loadingDriversOnUnits = false;
            LogManagerConsole('CATALOG', 'enrichUnitsWithDrivers - fulfilled', payload);
        })
        builder.addCase(enrichUnitsWithDrivers.rejected, (state, action) => {
            state.loadingDriversOnUnits = false;
            state.error = action.error.message;
            LogManagerConsole('ERROR', 'enrichUnitsWithDrivers - rejected', action.error);
        })

        builder.addCase(enrichUnitsWithAssets.pending, (state) => {
            state.loadingAssetsOnUnits = true;
            LogManagerConsole('CATALOG', 'enrichUnitsWithAssets - pending');
        })
        builder.addCase(enrichUnitsWithAssets.fulfilled, (state, { payload }) => {
            state.loadingAssetsOnUnits = false;
            LogManagerConsole('CATALOG', 'enrichUnitsWithAssets - fulfilled', payload);
        })
        builder.addCase(enrichUnitsWithAssets.rejected, (state, action) => {
            state.loadingAssetsOnUnits = false;
            state.error = action.error.message;
            LogManagerConsole('ERROR', 'enrichUnitsWithAssets - rejected', action.error);
        })

        // builder.addCase(processItems.pending, (state) => {
        //     //state.loading = true
        // })
        // builder.addCase(processItems.fulfilled, (state, { payload }) => {
        //     return { ...state, ...payload }
        // })
        // builder.addCase(processItems.rejected, (state, action) => {
        //     //state.loading = false
        //     //state.error = action.error.message
        // })

    },
})

// Extract and export the action creators object and the reducer
export const { actions, reducer } = catalogsRealtimeRedux

// Extract and export each action creator by name
export const { reset, setTagsProgress, loadLandmarks, loadGeofences } = actions

//Simple async action

// Export the reducer, either as a default or named export
export default reducer